import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateCompoundInterest, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const CompoundInterestCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [principal, setPrincipal] = useState(100000);
  const [rate, setRate] = useState(8);
  const [time, setTime] = useState(5);
  const [frequency, setFrequency] = useState(4);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, rate, time, frequency]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const p = Number(principal) || 0;
    const r = Number(rate) || 0;
    const t = Number(time) || 0;
    const f = Number(frequency) || 4;
    const ciResult = calculateCompoundInterest(p, r, t, f);
    setResult(ciResult);
  };

  const handleDownloadPDF = () => {
    const frequencyText = {
      1: 'Yearly',
      2: 'Half-Yearly',
      4: 'Quarterly',
      12: 'Monthly',
      365: 'Daily'
    };
    generateGenericPDF('Compound Interest Report', {
      'Principal Amount': principal,
      'Interest Rate': `${rate}% p.a.`,
      'Time Period': `${time} years`,
      'Compounding': frequencyText[frequency],
      'Compound Interest': result.interest,
      'Total Amount': result.totalAmount,
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setRate(8);
    setTime(5);
    setFrequency(4);
  };

  const chartData = result ? {
    labels: ['Principal', 'Interest Earned'],
    datasets: [{
      data: [result.principal, result.interest],
      backgroundColor: ['#3b82f6', '#10b981'],
      borderColor: ['#2563eb', '#059669'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Compound Interest Calculator"
        description="Free Compound Interest Calculator - Calculate CI with different compounding frequencies: monthly, quarterly, yearly."
        keywords="compound interest calculator, CI calculator, compound interest formula, monthly compounding, quarterly compounding, yearly compounding"
        canonicalPath="/compound-interest"
      />
      <div className="calculator-header">
        <h1>Compound Interest Calculator</h1>
        <p>Calculate compound interest with different compounding frequencies</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Principal Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
                max="100000000"
              />
            </div>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="10000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((principal || 0) / 10000000) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={rate === '' ? '' : rate}
              onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="0.1"
              max="50"
              step="0.1"
            />
            <div className="slider-container">
              <input
                type="range"
                min="0.1"
                max="30"
                step="0.1"
                value={rate || 0}
                onChange={(e) => setRate(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((rate || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Time Period (Years)</label>
            <input
              type="number"
              value={time === '' ? '' : time}
              onChange={(e) => setTime(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="50"
            />
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={time || 0}
                onChange={(e) => setTime(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ '--value': `${((time || 0) / 30) * 100}%` }}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Compounding Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(Number(e.target.value))}
            >
              <option value={365}>Daily</option>
              <option value={12}>Monthly</option>
              <option value={4}>Quarterly</option>
              <option value={2}>Half-Yearly</option>
              <option value={1}>Yearly</option>
            </select>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>Total Amount</h3>
                <div className="amount">{formatCurrency(result.totalAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Amount</span>
                  <span className="value">{formatCurrency(result.principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Compound Interest</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(result.interest)}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Effective Annual Rate</span>
                  <span className="value">
                    {((Math.pow(1 + rate / (100 * frequency), frequency) - 1) * 100).toFixed(2)}%
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Growth Multiple</span>
                  <span className="value">
                    {(result.totalAmount / result.principal).toFixed(2)}x
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About Compound Interest</h4>
        <p>
          Compound Interest is calculated on both the principal and the accumulated interest. 
          The formula is:
        </p>
        <p style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '16px' }}>
          A = P × (1 + r/n)^(n×t)
        </p>
        <p style={{ marginTop: '8px' }}>
          Where P = Principal, r = Annual rate (decimal), n = Compounding frequency, t = Time in years
        </p>
        <p style={{ marginTop: '8px' }}>
          <strong>Power of Compounding:</strong> More frequent compounding leads to higher returns. 
          Daily compounding yields more than yearly compounding for the same rate.
        </p>
      </div>
    </div>
  );
};

export default CompoundInterestCalculator;
